import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { Activity, Loader2 } from "lucide-react";

// Score → color band. Green ≥ 70, amber 40-69, red < 40, muted for zero-activity.
function cellStyle(score) {
  if (score === 0) return { bg: "bg-muted", fg: "text-muted-foreground/50", label: "—" };
  if (score >= 70) return { bg: "bg-emerald-500/25 border border-emerald-500/50", fg: "text-emerald-800", label: String(score) };
  if (score >= 40) return { bg: "bg-amber-500/25 border border-amber-500/50", fg: "text-amber-800", label: String(score) };
  return { bg: "bg-red-500/25 border border-red-500/60", fg: "text-red-800", label: String(score) };
}

export default function HealthDriftHeatmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/platform/health-drift")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load drift heatmap"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="p-5 mb-6" data-testid="health-drift-loading">
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Computing 4-week health drift…
        </div>
      </Card>
    );
  }
  if (err) {
    return (
      <Card className="p-5 mb-6" data-testid="health-drift-error">
        <div className="text-sm text-red-600">{err}</div>
      </Card>
    );
  }

  const weeks = data?.weeks || [];
  const tenants = data?.tenants || [];

  return (
    <Card className="p-5 mb-6" data-testid="health-drift-heatmap">
      <div className="flex items-center gap-2 mb-1">
        <Activity className="w-4 h-4 text-[hsl(var(--gold))]" />
        <div className="font-mono-label text-muted-foreground text-[0.65rem] tracking-widest">HEALTH DRIFT · 4-WEEK VIEW</div>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-display text-2xl font-bold text-navy">Who's drifting toward churn?</h2>
        <div className="flex items-center gap-3 text-[0.7rem] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-500/40 border border-emerald-500/50" /> Healthy ≥70</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-amber-500/40 border border-amber-500/50" /> Slipping 40-69</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-red-500/40 border border-red-500/60" /> Critical &lt;40</span>
          <span className="flex items-center gap-1"><span className="relative inline-block w-3 h-3 rounded-sm bg-muted"><span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-600 border border-white" /></span> Auto-nudged</span>
        </div>
      </div>

      {tenants.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No active tenants yet — heatmap fills in once your first founding dealers go live.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="health-drift-table">
            <thead>
              <tr className="text-[0.6rem] font-mono-label tracking-widest text-muted-foreground">
                <th className="text-left px-2 py-2 w-56">Dealer</th>
                {weeks.map((w, i) => (
                  <th key={i} className="text-center px-2 py-2">
                    <div>{w.label}</div>
                    <div className="normal-case font-normal text-[0.55rem] text-muted-foreground/70">
                      {i === weeks.length - 1 ? "this wk" : `${weeks.length - 1 - i}w ago`}
                    </div>
                  </th>
                ))}
                <th className="text-center px-2 py-2">Trend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {tenants.map((t) => {
                const first = t.cells[0]?.score ?? 0;
                const last = t.cells[t.cells.length - 1]?.score ?? 0;
                const delta = last - first;
                const trendColor = delta > 5 ? "text-emerald-700" : delta < -5 ? "text-red-700" : "text-muted-foreground";
                const trendLabel = delta > 5 ? `+${delta}` : delta < -5 ? `${delta}` : "flat";
                return (
                  <tr key={t.id} data-testid={`drift-row-${t.id}`} className="hover:bg-muted/20">
                    <td className="px-2 py-2 font-semibold text-navy">
                      <div>{t.name}</div>
                      <div className="text-[0.6rem] font-mono text-muted-foreground/70">{t.id}</div>
                    </td>
                    {t.cells.map((c, i) => {
                      const s = cellStyle(c.score);
                      return (
                        <td key={i} className="px-2 py-2 text-center">
                          <div
                            className={`relative inline-flex items-center justify-center w-12 h-12 rounded-md font-bold text-xs tabular-nums ${s.bg} ${s.fg}`}
                            title={`Score ${c.score} · ${c.sms} SMS · $${Math.round(c.revenue).toLocaleString()} revenue · ${c.appts} appts${c.drift_fired ? " · AUTO-NUDGED (drift alert fired)" : ""}`}
                            data-testid={`drift-cell-${t.id}-${i}`}
                          >
                            {s.label}
                            {c.drift_fired && (
                              <span
                                className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-600 border-2 border-white shadow"
                                data-testid={`drift-cell-badge-${t.id}-${i}`}
                                aria-label="drift alert fired"
                              />
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className={`px-2 py-2 text-center font-mono-label text-xs tabular-nums ${trendColor}`} data-testid={`drift-trend-${t.id}`}>
                      {trendLabel}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[0.7rem] text-muted-foreground mt-3">
        Weekly score (0-100): 35 pts SMS activity + 35 pts revenue recovered + 30 pts appointments booked. Hover any cell for the raw numbers.
      </div>
    </Card>
  );
}
